/**
 * Arena invitations — operational join intents referencing Arena + Player.
 * Not an ADR-028 aggregate root; does not alter Arena/Player/Match models.
 */

export const ARENA_INVITATION_STATUSES = Object.freeze({
  PENDING: "pending",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
});

export const ARENA_INVITATION_STATUS_LABELS = Object.freeze({
  pending: "Invitation sent",
  accepted: "Joined",
  declined: "Declined",
  expired: "Expired",
  cancelled: "Cancelled",
});

/** Default TTL: 7 days */
export const ARENA_INVITATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function generateArenaInvitationId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `ainv_${crypto.randomUUID().replace(/-/g, "").slice(0, 20)}`;
  }
  return `ainv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {object} input
 */
export function buildArenaInvitationDocument(input) {
  const now = Date.now();
  const expiresAtMs =
    typeof input.expiresAtMs === "number"
      ? input.expiresAtMs
      : now + ARENA_INVITATION_TTL_MS;
  return {
    invitationId: input.invitationId || generateArenaInvitationId(),
    arenaId: String(input.arenaId || "").trim(),
    arenaName: String(input.arenaName || input.arenaId || "").trim(),
    playerId: input.playerId || null,
    playerName: input.playerName || null,
    invitedUserId: input.invitedUserId || null,
    invitedBy: input.invitedBy,
    invitedByName: input.invitedByName || null,
    /** `player` = named player invite; `referee` = match official; `link` = shareable QR */
    kind: input.kind === "link" ? "link" : input.kind === "referee" ? "referee" : "player",
    status: ARENA_INVITATION_STATUSES.PENDING,
    createdAtMs: now,
    expiresAtMs,
    acceptedAtMs: null,
    acceptedByUid: null,
    declinedAtMs: null,
  };
}

/**
 * @param {object | null | undefined} inv
 * @param {number} [nowMs]
 */
export function isArenaInvitationExpired(inv, nowMs = Date.now()) {
  if (!inv) return true;
  if (inv.status === ARENA_INVITATION_STATUSES.EXPIRED) return true;
  const exp = Number(inv.expiresAtMs);
  return Number.isFinite(exp) && exp > 0 && nowMs > exp;
}

/**
 * @param {object | null | undefined} inv
 * @param {number} [nowMs]
 */
export function resolveArenaInvitationDisplayStatus(inv, nowMs = Date.now()) {
  if (!inv) return ARENA_INVITATION_STATUSES.EXPIRED;
  if (
    inv.status === ARENA_INVITATION_STATUSES.PENDING &&
    isArenaInvitationExpired(inv, nowMs)
  ) {
    return ARENA_INVITATION_STATUSES.EXPIRED;
  }
  return inv.status || ARENA_INVITATION_STATUSES.EXPIRED;
}

export function arenaInvitationJoinPath(invitationId) {
  const id = String(invitationId || "").trim();
  if (!id) return "/";
  const base = (typeof process !== "undefined" && process.env?.PUBLIC_URL
    ? String(process.env.PUBLIC_URL)
    : ""
  ).replace(/\/$/, "");
  return `${base}/join/${encodeURIComponent(id)}`;
}
